/**
 * Onboarding document upload/download wrappers. Documents persist on the
 * backend (S3 when `compliance.s3Bucket` is configured; local-fs fallback
 * to `.data/compliance/` in offline dev). The returned `storageUri` is
 * what the FE threads into each step payload's matching `…DocumentUri`
 * field — no FE-side knowledge of the storage backend required.
 */

import { orgAuthFetch } from "@solomon-apps/shared";

import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";

import { OrgApiError, orgApiFetch, orgApiFetchVoid } from "./orgApiFetch";

export type OnboardingDocumentPurpose =
    | "formation_document"
    | "operating_document"
    | "certificate_of_good_standing"
    | "tax_id_document"
    | "authorization_document"
    | "government_id"
    | "supporting_document";

export interface OnboardingDocumentView {
    uuid: string;
    applicationUuid: string;
    uploadedByOrgUserUuid: string;
    purpose: OnboardingDocumentPurpose;
    entryUuid: string | null;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    storageUri: string;
    createdAt: string;
}

const BASE = "/api/org/client/onboarding/applications";

interface UploadArgs {
    applicationUuid: string;
    purpose: OnboardingDocumentPurpose;
    entryUuid?: string;
    file: File;
}

/**
 * Upload one onboarding document. Multipart body — pure `orgAuthFetch`
 * (not `orgApiFetch`) because the helper hardcodes JSON; we let the
 * browser set the multipart boundary by omitting `Content-Type`.
 */
export async function uploadOnboardingDocument(args: UploadArgs): Promise<OnboardingDocumentView> {
    const formData = new FormData();
    // Field order is not load-bearing: the API registers @fastify/multipart
    // with `attachFieldsToBody: true`, so every part lands on `request.body`
    // as a record and the handler reads `purpose` / `entryUuid` from there
    // rather than off a partially-consumed stream. Kept in this order because
    // it reads naturally, not because the wire requires it.
    formData.append("purpose", args.purpose);
    if (args.entryUuid) {
        formData.append("entryUuid", args.entryUuid);
    }
    formData.append("file", args.file);
    const url = `${CLIENT_OAUTH_CONFIG.apiBase}${BASE}/${args.applicationUuid}/documents`;
    const response = await orgAuthFetch(url, { method: "POST", body: formData }, CLIENT_OAUTH_CONFIG);
    if (!response.ok) {
        const body = (await response.json().catch((): null => null)) as {
            code?: string;
            error?: string;
            message?: string;
        } | null;
        throw new OrgApiError(
            body?.message ?? body?.error ?? `Upload failed: ${response.status}`,
            response.status,
            body?.code
        );
    }
    const json = (await response.json()) as { document: OnboardingDocumentView };
    return json.document;
}

interface ListArgs {
    applicationUuid: string;
    purpose?: OnboardingDocumentPurpose;
    entryUuid?: string;
}

export async function listOnboardingDocuments(args: ListArgs): Promise<OnboardingDocumentView[]> {
    const params = new URLSearchParams();
    if (args.purpose) params.set("purpose", args.purpose);
    if (args.entryUuid) params.set("entryUuid", args.entryUuid);
    const qs = params.toString();
    const path = `${BASE}/${args.applicationUuid}/documents${qs ? `?${qs}` : ""}`;
    const result = await orgApiFetch<{ documents: OnboardingDocumentView[] }>(path);
    return result.documents;
}

export function softDeleteOnboardingDocument(args: { applicationUuid: string; documentUuid: string }): Promise<void> {
    return orgApiFetchVoid(`${BASE}/${args.applicationUuid}/documents/${args.documentUuid}`, { method: "DELETE" });
}

/**
 * Resolve the download URL for an applicant viewing their own
 * upload (e.g. previewing a previously-uploaded file inline). The
 * underlying endpoint streams the bytes with the original
 * `Content-Type` + `Content-Disposition: inline; filename=...`.
 */
export function buildOnboardingDocumentDownloadUrl(args: { applicationUuid: string; documentUuid: string }): string {
    return `${CLIENT_OAUTH_CONFIG.apiBase}${BASE}/${args.applicationUuid}/documents/${args.documentUuid}`;
}
